import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
import {
  Button,
  Checkbox,
  Col,
  Pane,
  PaneFooter,
  Paneset,
  Row,
  Select,
  TextField,
  TextArea,
} from '@folio/stripes/components';
import stripesFinalForm from '@folio/stripes/final-form';
import { Field, FormSpy } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { AppIcon } from '@folio/stripes-core';
import LocalizedTextField from '../../common/LocalizedTextField';
import WidgetReportData from './components/WidgetReportData';

function WidgetForm({
  submitting,
  handleSubmit,
  form,
  handleClose,
  editMode,
  initialValues,
  currentDashName,
  dataSource,
  setDataSource,
  isMultiReport,
  setIsMultiReport,
  selectedReports,
  setSelectedReports,
  pendingQuery,
  setPendingQuery,
  widgetDisplayMethod,
  setWidgetDisplayMethod,
  widgetVisibleColumns,
  setWidgetVisibleColumns,
  reportDisplayMethods,
  setReportDisplayMethods,
  onReportSelected,
  onNavigateToBuilder,
  isLoading,
}) {
  const renderPaneFooter = () => (
    <PaneFooter
      id="widget-form-pane-footer"
      renderEnd={
        <Button
          buttonStyle="primary"
          disabled={submitting || isLoading}
          id="widget-form-pane-save-btn"
          marginBottom0
          type="submit"
          onClick={async (e) => {
            e.preventDefault();
            await handleSubmit();
            form.reset();
            handleClose();
          }}
        >
          <FormattedMessage id="ui-reports.dashboards.widgetForm.save.btn" defaultMessage="Save & Close" />
        </Button>
      }
      renderStart={
        <Button
          buttonStyle="default"
          id="widget-form-pane-cancel-btn"
          marginBottom0
          onClick={() => handleClose()}
        >
          <FormattedMessage id="ui-reports.dashboards.widgetForm.cancel.btn" defaultMessage="Cancel" />
        </Button>
      }
    />
  );

  return (
    <Paneset>
      <Pane
        appIcon={<AppIcon app="reports" />}
        dismissible
        onClose={() => handleClose()}
        footer={renderPaneFooter()}
        id="widget-form-pane"
        centerContent
        paneTitle={
          editMode
            ? <FormattedMessage id="ui-reports.dashboards.widgetForm.paneTitle.editMode" defaultMessage="Edit Widget" />
            : <FormattedMessage id="ui-reports.dashboards.widgets.addNewWidget.btn" defaultMessage="Create new widget" />
        }
      >
        <form id="widget-form" onSubmit={handleSubmit}>

          {/* ── Data Source (top) ── */}
          <FormSpy subscription={{ values: true }}>
            {({ values: formValues }) => (
              <WidgetReportData
                dataSource={dataSource}
                setDataSource={setDataSource}
                isMultiReport={isMultiReport}
                setIsMultiReport={setIsMultiReport}
                selectedReports={selectedReports}
                setSelectedReports={setSelectedReports}
                pendingQuery={pendingQuery}
                setPendingQuery={setPendingQuery}
                widgetDisplayMethod={widgetDisplayMethod}
                setWidgetDisplayMethod={setWidgetDisplayMethod}
                widgetVisibleColumns={widgetVisibleColumns}
                setWidgetVisibleColumns={setWidgetVisibleColumns}
                reportDisplayMethods={reportDisplayMethods}
                setReportDisplayMethods={setReportDisplayMethods}
                onReportSelected={(report) => {
                  if (onReportSelected) onReportSelected(report);
                  // Update form fields with report name/desc
                  form.change('name', report.name);
                  form.change('desc', report.desc);
                }}
                onNavigateToBuilder={() => onNavigateToBuilder && onNavigateToBuilder(formValues)}
              />
            )}
          </FormSpy>

          {/* ── Widget metadata ── */}
          <Row>
            <Col xs={12}>
              <FieldArray
                name="name"
                component={LocalizedTextField}
                fieldComponent={TextField}
                fieldLabel={<FormattedMessage id="ui-reports.dashboards.widgetForm.widgetNameField.label" defaultMessage="Widget name" />}
                isRequired
              />
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <FieldArray
                name="desc"
                component={LocalizedTextField}
                fieldComponent={TextArea}
                fieldLabel={<FormattedMessage id="ui-reports.dashboards.widgetForm.widgetDescField.label" defaultMessage="Widget description" />}
                isRequired
              />
            </Col>
          </Row>

          {/* ── Settings row ── */}
          <Row>
            <Col xs={4}>
              <TextField
                id="form-widget-dashboard-name"
                label={<FormattedMessage id="ui-reports.dashboards.widgetForm.dashNameField.label" defaultMessage="Dashboard name" />}
                value={currentDashName}
                disabled
              />
            </Col>
            <Col xs={2}>
              <Field
                id="form-widget-display-order"
                name="displayOrder"
                component={TextField}
                type="number"
                label={<FormattedMessage id="ui-reports.dashboards.widgetForm.orderField.label" defaultMessage="Display order" />}
              />
            </Col>
            <Col xs={2}>
              <Field
                id="form-widget-layout-w"
                name="layoutW"
                component={Select}
                label={<FormattedMessage id="ui-reports.dashboards.widgetForm.layoutW.label" defaultMessage="Width (cols)" />}
                dataOptions={[
                  { value: 3, label: '3 (25%)' },
                  { value: 4, label: '4 (33%)' },
                  { value: 6, label: '6 (50%)' },
                  { value: 8, label: '8 (66%)' },
                  { value: 12, label: '12 (100%)' },
                ]}
              />
            </Col>
            <Col xs={2}>
              <Field
                name="isActive"
                component={Checkbox}
                type="checkbox"
                label={<FormattedMessage id="ui-reports.dashboards.widgetForm.isActive.label" />}
              />
            </Col>
            <Col xs={2}>
              <Field
                name="isVisible"
                component={Checkbox}
                type="checkbox"
                label={<FormattedMessage id="ui-reports.dashboards.widgetForm.isVisible.label" />}
              />
            </Col>
          </Row>
        </form>
      </Pane>
    </Paneset>
  );
}

WidgetForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  submitting: PropTypes.bool,
  form: PropTypes.object.isRequired,
  handleClose: PropTypes.func.isRequired,
  initialValues: PropTypes.object.isRequired,
  editMode: PropTypes.bool.isRequired,
  currentDashName: PropTypes.string.isRequired,
  dataSource: PropTypes.oneOf(['reports', 'query']).isRequired,
  setDataSource: PropTypes.func.isRequired,
  isMultiReport: PropTypes.bool.isRequired,
  setIsMultiReport: PropTypes.func.isRequired,
  selectedReports: PropTypes.array.isRequired,
  setSelectedReports: PropTypes.func.isRequired,
  pendingQuery: PropTypes.object,
  setPendingQuery: PropTypes.func.isRequired,
  widgetDisplayMethod: PropTypes.string.isRequired,
  setWidgetDisplayMethod: PropTypes.func.isRequired,
  widgetVisibleColumns: PropTypes.array.isRequired,
  setWidgetVisibleColumns: PropTypes.func.isRequired,
  reportDisplayMethods: PropTypes.array.isRequired,
  setReportDisplayMethods: PropTypes.func.isRequired,
  onReportSelected: PropTypes.func,
  onNavigateToBuilder: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default stripesFinalForm({
  mutators: {},
  validateOnBlur: true,
  navigationCheck: false,
  subscription: { values: true },
})(WidgetForm);
